import React, { useState, useEffect } from 'react';
import { Search, ShoppingCart, User, ChevronDown, Package, LogOut, Menu, X } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { useSettings } from '../contexts/SettingsContext';
import { SearchModal } from './SearchModal';
import { authApi, categoriesApi, productsApi } from '../lib/api';
import { mapCategory, mapProduct } from '../lib/mappers';
import { Category, Product } from '../types';
import { navigate } from '../lib/router';

interface CategoryWithProducts extends Category {
  products?: Product[];
}

export const Header: React.FC = () => {
  const { cartCount, setIsCartOpen } = useCart();
  const { settings } = useSettings();
  const [isShopOpen, setIsShopOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isHomePage, setIsHomePage] = useState(() => window.location.pathname === '/');
  const [infoBarHeight, setInfoBarHeight] = useState(0);
  const [headerOffset, setHeaderOffset] = useState(0);
  const [user, setUser] = useState<any>(null);
  const [categoriesWithProducts, setCategoriesWithProducts] = useState<CategoryWithProducts[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [categoriesLoaded, setCategoriesLoaded] = useState(false);
  const [shopDropdownTimeout, setShopDropdownTimeout] = useState<number | null>(null);
  const [hasHeaderLogoError, setHasHeaderLogoError] = useState(false);
  const [hasHeroLogoError, setHasHeroLogoError] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  // Track scroll for header treatment + hash for home detection
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > infoBarHeight + 8);
      setHeaderOffset(Math.max(infoBarHeight - window.scrollY, 0));
    };
    const handleHash = () => setIsHomePage(window.location.pathname === '/');
    handleScroll();
    handleHash();
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('popstate', handleHash);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('popstate', handleHash);
    };
  }, [infoBarHeight]);

  // Measure announcement bar height so header sits beneath it
  useEffect(() => {
    const measureBar = () => {
      const el = document.getElementById('announcement-bar');
      setInfoBarHeight(el?.offsetHeight || 0);
      setHeaderOffset(el?.offsetHeight || 0);
    };
    measureBar();
    window.addEventListener('resize', measureBar);
    return () => window.removeEventListener('resize', measureBar);
  }, []);

  // Close user menu when clicking outside
  useEffect(() => {
    if (!isUserMenuOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.user-menu-container')) {
        setIsUserMenuOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isUserMenuOpen]);

  // Lock body scroll and close on Escape when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === 'Escape') setIsMobileMenuOpen(false);
      };
      document.addEventListener('keydown', handleEscape);
      return () => {
        document.body.style.overflow = '';
        document.removeEventListener('keydown', handleEscape);
      };
    } else {
      document.body.style.overflow = '';
      return undefined;
    }
  }, [isMobileMenuOpen]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (shopDropdownTimeout) {
        window.clearTimeout(shopDropdownTimeout);
      }
    };
  }, [shopDropdownTimeout]);

  useEffect(() => {
    setHasHeaderLogoError(false);
    setHasHeroLogoError(false);
  }, [settings?.headerLogo, settings?.heroLogo]);

  const fetchCategoriesWithProducts = async () => {
    if (categoriesLoaded || loadingCategories) return;
    try {
      setLoadingCategories(true);
      
      // Get all categories
      const { data: categoriesData, error: categoriesError } = await categoriesApi.getAll();
      if (categoriesError) {
        console.error('Failed to load categories:', categoriesError);
        setLoadingCategories(false);
        return;
      }

      const categories = (categoriesData?.categories || []).map(mapCategory);
      
      // Get main categories (no parent)
      const mainCategories = categories.filter(cat => !cat.parent_id && cat.isActive !== false);
      
      // For each main category, get products
      const categoriesWithProductsData = await Promise.all(
        mainCategories.map(async (category) => {
          try {
            const { data: productsData, error: productsError } = await productsApi.getAll({
              category: category.id,
              limit: 10,
            });
            
            if (productsError) {
              console.error(`Failed to load products for ${category.name}:`, productsError);
              return { ...category, products: [] };
            }
            
            const products = (productsData?.products || []).map(mapProduct);
            return { ...category, products };
          } catch (err) {
            console.error(`Error loading products for ${category.name}:`, err);
            return { ...category, products: [] };
          }
        })
      );
      
      setCategoriesWithProducts(categoriesWithProductsData);
    } catch (error) {
      console.error('Error fetching categories with products:', error);
    } finally {
      setCategoriesLoaded(true);
      setLoadingCategories(false);
    }
  };

  const checkAuth = async () => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      try {
        const { data, error } = await authApi.getProfile();
        if (!error && data?.user) {
          setUser(data.user);
        } else {
          localStorage.removeItem('auth_token');
        }
      } catch (err) {
        localStorage.removeItem('auth_token');
      }
    }
  };

  const handleLogout = () => {
    authApi.logout();
    setUser(null);
    setIsUserMenuOpen(false);
    navigate('/');
  };

  const isOverlayHeader = isHomePage && !isScrolled;
  const headerBg = isOverlayHeader
    ? 'bg-transparent shadow-none'
    : 'bg-[rgba(245,240,230,0.96)] border-b border-primary/12 shadow-[0_14px_36px_rgba(13,13,13,0.06)] backdrop-blur-md';
  const linkBase = isOverlayHeader ? 'text-white' : 'text-text';
  const hoverAccent = isOverlayHeader ? 'hover:text-white/80' : 'hover:text-primary';
  const iconColor = isOverlayHeader ? 'text-white' : 'text-text';
  const brandName = settings?.storeName || 'Silver Strix';
  const heroLogoSrc = settings?.heroLogo?.trim() || '';
  const headerLogoSrc = settings?.headerLogo?.trim() || '';
  const activeLogoSrc = isOverlayHeader
    ? (!hasHeroLogoError ? heroLogoSrc : '') || (!hasHeaderLogoError ? headerLogoSrc : '')
    : (!hasHeaderLogoError ? headerLogoSrc : '') || (!hasHeroLogoError ? heroLogoSrc : '');
  const showActiveLogo = Boolean(activeLogoSrc);
  const iconButtonClass = isOverlayHeader
    ? `inline-flex h-11 w-11 shrink-0 items-center justify-center ${iconColor} ${hoverAccent} rounded-full border border-white/15 bg-black/20 backdrop-blur-sm transition-colors hover:bg-black/30`
    : `inline-flex h-11 w-11 shrink-0 items-center justify-center ${iconColor} ${hoverAccent} rounded-full border border-primary/10 bg-white/40 transition-colors hover:bg-white`;
  const userButtonClass = isOverlayHeader
    ? `flex items-center gap-2 rounded-full border border-white/15 bg-black/20 px-3 py-2 ${iconColor} ${hoverAccent} backdrop-blur-sm transition-colors hover:bg-black/30`
    : `flex items-center gap-2 rounded-full border border-primary/10 bg-white/40 px-3 py-2 ${iconColor} ${hoverAccent} transition-colors hover:bg-white`;

  const renderBrand = (compact = false) => (
    <a
      href="/"
      className={`group flex items-center justify-center ${compact ? 'h-14 w-14' : 'w-full justify-center'}`}
      onClick={() => setIsMobileMenuOpen(false)}
      aria-label={brandName}
    >
      {showActiveLogo ? (
        <img
          src={activeLogoSrc}
          alt={brandName}
          onError={() => {
            if (activeLogoSrc === heroLogoSrc) {
              setHasHeroLogoError(true);
            }
            if (activeLogoSrc === headerLogoSrc) {
              setHasHeaderLogoError(true);
            }
          }}
          className={compact ? 'h-12 w-auto max-w-[4.5rem] object-contain' : 'h-[4.25rem] w-auto max-w-[11rem] object-contain'}
        />
      ) : (
        <span className={`inline-flex items-center transition-transform duration-300 group-hover:-translate-y-0.5 ${compact ? 'h-12 w-12 justify-center rounded-full p-0' : 'gap-3 rounded-full px-3.5 py-2.5'} ${isOverlayHeader ? 'border border-white/15 bg-black/20 shadow-[0_16px_40px_rgba(0,0,0,0.22)] backdrop-blur-sm' : 'border border-primary/10 bg-white/80 shadow-[0_16px_40px_rgba(13,13,13,0.08)]'}`}>
          <span className={`flex h-10 w-10 items-center justify-center rounded-full text-sm font-semibold uppercase tracking-[0.18em] text-white ${isOverlayHeader ? 'bg-white/18' : 'bg-primary'}`}>
            S
          </span>
          {!compact && (
            <span className="hidden min-w-0 sm:flex flex-col text-left leading-tight">
              <span className={`text-[0.62rem] font-semibold uppercase tracking-[0.28em] ${isOverlayHeader ? 'text-white/60' : 'text-primary/55'}`}>
                Fragrance House
              </span>
              <span className={`text-base font-semibold ${isOverlayHeader ? 'text-white' : 'text-text'}`}>
                {brandName}
              </span>
            </span>
          )}
        </span>
      )}
    </a>
  );

  return (
    <>
      <header
        className={`fixed left-0 right-0 z-50 transition-all duration-300 ${headerBg}`}
        style={{ top: `${headerOffset}px` }}
      >
        <div className="container mx-auto px-4 lg:px-8">
          <div className="hidden items-center gap-4 py-4 lg:flex">
            {/* Left: Navigation */}
            <nav className="flex flex-1 items-center gap-8">
              <div
                className="relative shop-dropdown-container"
                onMouseEnter={() => {
                  if (shopDropdownTimeout) {
                    window.clearTimeout(shopDropdownTimeout);
                    setShopDropdownTimeout(null);
                  }
                  fetchCategoriesWithProducts();
                  setIsShopOpen(true);
                }}
                onMouseLeave={() => {
                  const timeout = window.setTimeout(() => {
                    setIsShopOpen(false);
                  }, 200); // Small delay before closing
                  setShopDropdownTimeout(timeout);
                }}
              >
                <button
                  className={`flex items-center gap-1 font-medium transition-colors ${linkBase} ${hoverAccent} ${
                    isShopOpen ? 'text-primary' : ''
                  }`}
                  onClick={(e) => {
                    e.preventDefault();
                    fetchCategoriesWithProducts();
                    setIsShopOpen((prev) => !prev);
                  }}
                >
                  Shop
                  <ChevronDown size={16} className={`transition-transform duration-200 ${isShopOpen ? 'rotate-180' : ''}`} />
                </button>

                {isShopOpen && (
                  <div 
                    className="absolute left-0 top-full z-50 mt-3 w-[900px] rounded-[28px] border border-primary/10 bg-background p-8 shadow-[0_28px_60px_rgba(13,13,13,0.12)] animate-fade-in"
                    onMouseEnter={() => {
                      if (shopDropdownTimeout) {
                        window.clearTimeout(shopDropdownTimeout);
                        setShopDropdownTimeout(null);
                      }
                      setIsShopOpen(true);
                    }}
                    onMouseLeave={() => {
                      const timeout = window.setTimeout(() => {
                        setIsShopOpen(false);
                      }, 200);
                      setShopDropdownTimeout(timeout);
                    }}
                  >
                    {loadingCategories ? (
                      <div className="text-center py-8">
                        <p className="text-charcoal/70">Loading categories...</p>
                      </div>
                    ) : categoriesWithProducts.length > 0 ? (
                      <div className="grid grid-cols-5 gap-8">
                        {categoriesWithProducts.map((category) => (
                          <div key={category.id}>
                            <a
                              href={`/category/${category.slug || category.id}`}
                              className="mb-3 block text-sm font-semibold uppercase tracking-[0.12em] text-primary transition-colors hover:text-primary-dark"
                              onClick={() => setIsShopOpen(false)}
                            >
                              {category.name}
                            </a>
                            <ul className="space-y-2">
                              {category.products && category.products.length > 0 ? (
                                <>
                                  {category.products.map((product) => (
                                    <li key={product.id}>
                                      <a
                                        href={`/product/${product.id || product.slug}`}
                                        className="block py-1 text-sm text-charcoal/75 transition-colors hover:text-text"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          setIsShopOpen(false);
                                          navigate(`/product/${product.id || product.slug}`);
                                        }}
                                      >
                                        {product.name}
                                      </a>
                                    </li>
                                  ))}
                                </>
                              ) : (
                                <li className="text-sm italic text-charcoal/45">No products yet</li>
                              )}
                            </ul>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <p className="text-charcoal/70">No categories available</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <a href="/bestsellers" className={`${linkBase} font-medium ${hoverAccent} transition-colors`}>
                Best Seller
              </a>

              <a href="/contact" className={`${linkBase} font-medium ${hoverAccent} transition-colors`}>
                Contact Us
              </a>
            </nav>

            {/* Center: Logo */}
            <div className="flex w-[220px] flex-none items-center justify-center">
              {renderBrand()}
            </div>

            {/* Right: Search / Profile / Cart */}
            <div className="flex flex-1 items-center justify-end gap-4">
              <button
                onClick={() => setIsSearchOpen(true)}
                className={iconButtonClass}
              >
                <Search size={20} />
              </button>

              <div className="relative user-menu-container">
                {user ? (
                  <>
                    <button
                      onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                      className={userButtonClass}
                    >
                      <User size={20} />
                      <span className="hidden md:inline text-sm">{user.name || user.email}</span>
                      <ChevronDown size={16} />
                    </button>
                    {isUserMenuOpen && (
                      <div className="absolute right-0 top-full z-50 mt-3 w-56 rounded-2xl border border-primary/10 bg-background p-4 shadow-[0_20px_44px_rgba(13,13,13,0.12)] animate-fade-in">
                        <div className="mb-3 border-b border-primary/10 pb-3">
                          <p className="font-semibold text-text">{user.name || 'User'}</p>
                          <p className="text-sm text-charcoal/70">{user.email}</p>
                        </div>
                        <a
                          href="/profile"
                          onClick={() => setIsUserMenuOpen(false)}
                          className="flex items-center gap-2 py-2 text-text transition-colors hover:text-primary"
                        >
                          <User size={18} />
                          My Profile
                        </a>
                        <a
                          href="/orders"
                          onClick={() => setIsUserMenuOpen(false)}
                          className="flex items-center gap-2 py-2 text-text transition-colors hover:text-primary"
                        >
                          <Package size={18} />
                          My Orders
                        </a>
                        <button
                          onClick={handleLogout}
                          className="w-full flex items-center gap-2 text-red-600 hover:text-red-700 transition-colors py-2 mt-2"
                        >
                          <LogOut size={18} />
                          Logout
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <a href="/login" className={iconButtonClass} aria-label="Login">
                    <User size={20} />
                  </a>
                )}
              </div>

              <button
                onClick={() => setIsCartOpen(true)}
                className={`relative ${iconButtonClass}`}
              >
                <ShoppingCart size={20} />
                {cartCount > 0 && (
                  <span className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-xs font-bold text-white">
                    {cartCount}
                  </span>
                )}
              </button>

              <button
                type="button"
                onClick={() => {
                  const nextValue = !isMobileMenuOpen;
                  if (nextValue) {
                    fetchCategoriesWithProducts();
                  }
                  setIsMobileMenuOpen(nextValue);
                }}
                className={`rounded-full border border-primary/10 bg-white/40 p-2.5 transition-colors hover:bg-white lg:hidden ${iconColor} ${hoverAccent}`}
                aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
              >
                {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>

          <div className="relative flex items-center justify-between gap-3 py-3 lg:hidden">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setIsSearchOpen(true)}
                className={iconButtonClass}
                aria-label="Open search"
              >
                <Search size={20} />
              </button>
            </div>

            <div className="pointer-events-none absolute inset-x-0 flex justify-center">
              <div className="pointer-events-auto">
                {renderBrand(true)}
              </div>
            </div>

            <div className="ml-auto flex items-center gap-3">
              <button
                onClick={() => setIsCartOpen(true)}
                className={`relative ${iconButtonClass}`}
                aria-label="Open cart"
              >
                <ShoppingCart size={20} />
                {cartCount > 0 && (
                  <span className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-xs font-bold text-white">
                    {cartCount}
                  </span>
                )}
              </button>

              <button
                type="button"
                onClick={() => {
                  const nextValue = !isMobileMenuOpen;
                  if (nextValue) {
                    fetchCategoriesWithProducts();
                  }
                  setIsMobileMenuOpen(nextValue);
                }}
                className={iconButtonClass}
                aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
              >
                {isMobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {!isHomePage && <div aria-hidden className="h-[88px]" />}

      {/* Mobile menu: backdrop + right-side drawer (only on small screens) */}
      <div className="lg:hidden">
        {isMobileMenuOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-[60] animate-fade-in"
            aria-hidden="true"
            onClick={() => setIsMobileMenuOpen(false)}
          />
        )}
        <div
          className={`fixed top-0 right-0 z-[70] flex h-full w-[min(340px,88vw)] max-w-full flex-col border-l border-primary/10 bg-background shadow-2xl transition-transform duration-300 ease-out lg:hidden ${
            isMobileMenuOpen ? 'translate-x-0 pointer-events-auto' : 'translate-x-full pointer-events-none'
          }`}
          aria-modal="true"
          aria-label="Navigation menu"
        >
          <div className="flex items-center justify-between border-b border-primary/10 p-4">
            <h2 className="text-lg font-semibold tracking-[0.12em] text-text uppercase">Menu</h2>
            <button
              type="button"
              onClick={() => setIsMobileMenuOpen(false)}
              className="rounded-full p-2 text-text transition-colors hover:bg-secondary hover:text-primary"
              aria-label="Close menu"
            >
              <X size={24} />
            </button>
          </div>
          <nav className="flex-1 overflow-y-auto py-4">
            <div className="border-b border-primary/10 px-4 pb-4">
              {user ? (
                <div className="rounded-2xl bg-white/70 px-4 py-4 shadow-[0_14px_36px_rgba(13,13,13,0.06)]">
                  <p className="font-semibold text-text">{user.name || 'User'}</p>
                  <p className="mt-1 text-sm text-charcoal/70">{user.email}</p>
                  <div className="mt-4 flex flex-col gap-1">
                    <a
                      href="/profile"
                      className="rounded-xl px-3 py-3 font-medium text-text transition-colors hover:bg-secondary hover:text-primary"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      My Profile
                    </a>
                    <a
                      href="/orders"
                      className="rounded-xl px-3 py-3 font-medium text-text transition-colors hover:bg-secondary hover:text-primary"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      My Orders
                    </a>
                    <button
                      onClick={handleLogout}
                      className="rounded-xl px-3 py-3 text-left font-medium text-red-600 transition-colors hover:bg-red-50 hover:text-red-700"
                    >
                      Logout
                    </button>
                  </div>
                </div>
              ) : (
                <a
                  href="/login"
                  className="block rounded-2xl bg-white/70 px-4 py-4 font-medium text-text shadow-[0_14px_36px_rgba(13,13,13,0.06)] transition-colors hover:text-primary"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Sign in to your account
                </a>
              )}
            </div>
            <div className="px-4 flex flex-col gap-1">
              <a
                href="/bestsellers"
                className="rounded-xl px-3 py-3 font-medium text-text transition-colors hover:bg-secondary hover:text-primary"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Best Seller
              </a>
              <a
                href="/products"
                className="rounded-xl px-3 py-3 font-medium text-text transition-colors hover:bg-secondary hover:text-primary"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Products
              </a>
              <a
                href="/contact"
                className="rounded-xl px-3 py-3 font-medium text-text transition-colors hover:bg-secondary hover:text-primary"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Contact Us
              </a>
            </div>
            <div className="mt-2 border-t border-primary/10 px-4 pt-4">
              <p className="px-3 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-charcoal/55">Shop by category</p>
              {loadingCategories ? (
                <p className="px-3 py-2 text-sm text-charcoal/55">Loading...</p>
              ) : (
                <ul className="space-y-0">
                  {categoriesWithProducts.map((category) => (
                    <li key={category.id}>
                      <a
                        href={`/category/${category.slug || category.id}`}
                        className="block rounded-xl px-3 py-3 font-medium text-text transition-colors hover:bg-secondary hover:text-primary"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        {category.name}
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </nav>
        </div>
      </div>

      <SearchModal isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
    </>
  );
};
